import {
  type MaintenanceChecklistSection,
  type MaintenanceProtocolEditor,
} from '@gb-ticket/shared-types';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { usePageTitle } from '@/hooks/use-page-title';
import { ApiRequestError } from '@/lib/api';
import { cn } from '@/lib/utils';
import { PlatformPageHeader } from '@/platform/components/platform-page-header';
import { ChecklistFieldInput } from '@/products/maintenance/features/protocol/checklist-field-input';
import { ProtocolAcroformSheet } from '@/products/maintenance/features/protocol/protocol-acroform-sheet';
import { ProtocolInkSheet } from '@/products/maintenance/features/protocol/protocol-ink-sheet';
import {
  clearProtocolDraft,
  readProtocolDraft,
  writeProtocolDraft,
} from '@/products/maintenance/features/protocol/protocol-draft-buffer';
import { protocolSectionIsComplete } from '@/products/maintenance/features/protocol/protocol-section-status';
import { ProtocolSignaturesSection } from '@/products/maintenance/features/protocol/protocol-signatures-section';
import { ProtocolWizardContextCard } from '@/products/maintenance/features/protocol/protocol-wizard-context-card';
import { TechnicianNotesCard } from '@/products/maintenance/features/protocol/technician-notes-card';
import {
  MAINTENANCE_PROTOCOL_COLUMN_CLASS,
  MAINTENANCE_PROTOCOL_PDF_COLUMN_CLASS,
} from '@/products/maintenance/features/protocol/protocol-wizard-layout';
import { ProtocolWizardProgress } from '@/products/maintenance/features/protocol/protocol-wizard-progress';
import {
  uploadMaintenanceProtocolAttachment,
  useFinalizeMaintenanceWorkOrder,
  useMaintenanceProtocol,
  useMaintenanceInkLayers,
  useUpdateMaintenanceProtocolNotes,
  useUpsertMaintenanceProtocolAnswer,
  useValidateMaintenanceProtocol,
} from '@/products/maintenance/features/protocol/use-maintenance-protocol';
import { useMaintenanceWorkOrder } from '@/products/maintenance/features/work-orders/use-maintenance-work-orders';
import { API_BASE_URL } from '@/lib/api';

type SaveState = 'idle' | 'dirty' | 'saving' | 'saved' | 'error';

type ProtocolWizardEditorProps = {
  protocolId: string;
  editor: MaintenanceProtocolEditor;
  onRefetch: () => void;
};

function WizardNavButtons({
  step,
  totalSteps,
  sectionsLength,
  stretch,
  onBack,
  onForward,
}: {
  step: number;
  totalSteps: number;
  sectionsLength: number;
  stretch?: boolean;
  onBack: () => void;
  onForward: () => void;
}) {
  const onLastSection = step === sectionsLength - 1;
  const forwardLabel = onLastSection ? 'Zur Zusammenfassung' : 'Weiter';
  const buttonClass = cn('min-h-11', stretch && 'flex-1');

  return (
    <>
      <Button
        type="button"
        variant="outline"
        className={buttonClass}
        disabled={step === 0}
        onClick={onBack}
      >
        Zurück
      </Button>
      <Button
        type="button"
        className={buttonClass}
        disabled={step >= totalSteps - 1}
        onClick={onForward}
      >
        {forwardLabel}
      </Button>
    </>
  );
}

function readStringProperty(value: object, key: string): unknown {
  return Object.getOwnPropertyDescriptor(value, key)?.value;
}

function unknownList(value: unknown): unknown[] {
  if (!Array.isArray(value)) {
    return [];
  }
  return value.flatMap((item: unknown) => [item]);
}

function protocolIssueMessages(error: ApiRequestError): string[] {
  const details = error.error.details;
  const raw =
    details && typeof details === 'object' ? readStringProperty(details, 'issues') : undefined;
  const messages: string[] = [];
  for (const item of unknownList(raw)) {
    if (!item || typeof item !== 'object') {
      continue;
    }
    const message = readStringProperty(item, 'message');
    if (typeof message === 'string' && message.trim()) {
      messages.push(message.trim());
    }
  }
  if (messages.length > 0) {
    return [...new Set(messages)];
  }
  return error.message.trim() ? [error.message.trim()] : [];
}

function ProtocolWizardEditor({ protocolId, editor, onRefetch }: ProtocolWizardEditorProps) {
  const workOrder = useMaintenanceWorkOrder(editor.workOrderId);
  const upsert = useUpsertMaintenanceProtocolAnswer(protocolId);
  const { mutateAsync: saveNotes } = useUpdateMaintenanceProtocolNotes(protocolId);
  const validate = useValidateMaintenanceProtocol(protocolId);
  const finalize = useFinalizeMaintenanceWorkOrder(editor.workOrderId);
  const [finalizeConsent, setFinalizeConsent] = useState(false);
  const [blockingIssues, setBlockingIssues] = useState<string[]>([]);

  const sections = useMemo(
    () =>
      editor.templateSnapshot.definition.sections.slice().sort((a, b) => a.sortOrder - b.sortOrder),
    [editor],
  );

  const [step, setStep] = useState(0);
  const [localValues, setLocalValues] = useState<Record<string, unknown>>(() =>
    buildLocalValues(editor, protocolId),
  );
  const [localComments, setLocalComments] = useState<Record<string, string | null>>(() =>
    buildLocalComments(editor, protocolId),
  );
  const [saveState, setSaveState] = useState<SaveState>('idle');
  const [revision, setRevision] = useState(editor.revision);
  const [revisionSync, setRevisionSync] = useState(editor.revision);
  const [notes, setNotes] = useState(editor.generalNotes ?? '');
  const [notesDirty, setNotesDirty] = useState(false);
  const [notesSyncKey, setNotesSyncKey] = useState(editor.generalNotes);

  if (editor.revision !== revisionSync) {
    setRevisionSync(editor.revision);
    setRevision(editor.revision);
  }
  if (!notesDirty && editor.generalNotes !== notesSyncKey) {
    setNotesSyncKey(editor.generalNotes);
    setNotes(editor.generalNotes ?? '');
  }

  const flushPendingSaves = useCallback(async () => {
    if (editor.workOrderStatus !== 'in_progress') {
      return;
    }
    setSaveState('saving');
    const draft = readProtocolDraft(protocolId);
    const keys = Object.keys(draft);
    try {
      let nextRevision = revision;
      for (const fieldKey of keys) {
        const entry = draft[fieldKey];
        if (!entry) {
          continue;
        }
        const res = await upsert.mutateAsync({
          fieldKey,
          body: { revision: nextRevision, value: entry.value, comment: entry.comment ?? null },
        });
        nextRevision = res.revision;
      }
      setRevision(nextRevision);
      clearProtocolDraft(protocolId);
      setSaveState('saved');
    } catch (err) {
      setSaveState('error');
      if (err instanceof ApiRequestError && err.error.statusCode === 409) {
        toast.error('Konflikt – bitte Seite neu laden.');
        onRefetch();
      } else {
        toast.error(err instanceof ApiRequestError ? err.message : 'Speichern fehlgeschlagen.');
      }
    }
  }, [editor.workOrderStatus, onRefetch, protocolId, revision, upsert]);

  useEffect(() => {
    if (saveState !== 'dirty') {
      return;
    }
    const handle = window.setTimeout(() => {
      void flushPendingSaves();
    }, 900);
    return () => window.clearTimeout(handle);
  }, [flushPendingSaves, localComments, localValues, saveState]);

  function queueFieldSave(fieldKey: string, value: unknown, comment?: string | null) {
    const draft = readProtocolDraft(protocolId);
    draft[fieldKey] = { value, comment };
    writeProtocolDraft(protocolId, draft);
    setSaveState('dirty');
  }

  function onValueChange(fieldKey: string, value: unknown) {
    setLocalValues((prev) => ({ ...prev, [fieldKey]: value }));
    queueFieldSave(fieldKey, value, localComments[fieldKey] ?? null);
  }

  function onCommentChange(fieldKey: string, comment: string | null) {
    setLocalComments((prev) => ({ ...prev, [fieldKey]: comment }));
    queueFieldSave(fieldKey, localValues[fieldKey], comment);
  }

  async function onFileSelected(fieldKey: string, file: File) {
    setSaveState('saving');
    try {
      await uploadMaintenanceProtocolAttachment(protocolId, file, fieldKey);
      setSaveState('saved');
      onRefetch();
    } catch (error: unknown) {
      setSaveState('error');
      toast.error(
        error instanceof ApiRequestError
          ? error.message
          : 'Anhang konnte nicht hochgeladen werden.',
      );
    }
  }

  const saveLabel =
    saveState === 'saving'
      ? 'Wird gespeichert …'
      : saveState === 'saved'
        ? 'Gespeichert'
        : saveState === 'dirty'
          ? 'Nicht gespeichert'
          : saveState === 'error'
            ? 'Fehler beim Speichern'
            : '';

  const readOnly = editor.workOrderStatus !== 'in_progress';

  const persistNotes = useCallback(async () => {
    if (readOnly) {
      return;
    }
    try {
      await saveNotes({
        generalNotes: notes.trim() ? notes.trim() : null,
      });
      setNotesDirty(false);
    } catch (error: unknown) {
      toast.error(
        error instanceof ApiRequestError
          ? error.message
          : 'Anmerkungen konnten nicht gespeichert werden.',
      );
    }
  }, [notes, readOnly, saveNotes]);

  useEffect(() => {
    if (!notesDirty || readOnly) {
      return;
    }
    const handle = window.setTimeout(() => {
      void persistNotes();
    }, 900);
    return () => window.clearTimeout(handle);
  }, [notesDirty, persistNotes, readOnly]);
  const isAcroform = editor.templateSnapshot.templateKind === 'pdf_acroform';
  const isInk = editor.templateSnapshot.templateKind === 'pdf_ink';
  const ink = useMaintenanceInkLayers(protocolId, isInk);
  const steps = isInk
    ? [
        {
          id: 'ink',
          title: 'Stift',
          sortOrder: 0,
          fields: [],
        },
        ...sections,
      ]
    : sections;
  const totalSteps = steps.length + 1;
  const isSummary = step >= steps.length;
  const currentSection: MaintenanceChecklistSection | undefined = steps[step];
  const order = workOrder.data;

  return (
    <div className="space-y-4 pb-24 sm:pb-6">
      <PlatformPageHeader title="Wartungsprotokoll" />

      {order ? (
        <ProtocolWizardContextCard
          orderNumber={order.orderNumber}
          workOrderStatus={editor.workOrderStatus}
          customerName={order.customer.name}
          installationLabel={`${order.installation.installationNumber} – ${order.installation.name}`}
          location={order.installation.location}
          saveLabel={saveLabel}
        />
      ) : null}

      <ProtocolWizardProgress
        sections={steps}
        step={step}
        isSummary={isSummary}
        onSelectStep={setStep}
      />

      {!isSummary && currentSection?.id === 'ink' && !ink.isLoading ? (
        <div className={MAINTENANCE_PROTOCOL_PDF_COLUMN_CLASS}>
          <ProtocolInkSheet
            protocolId={protocolId}
            pdfUrl={`${API_BASE_URL}/maintenance/protocols/${protocolId}/source.pdf`}
            revision={revision}
            layers={ink.data?.layers ?? []}
            disabled={readOnly}
            onSaved={setRevision}
            onConflict={onRefetch}
          />
        </div>
      ) : null}

      {!isSummary && currentSection && isAcroform ? (
        <div className={MAINTENANCE_PROTOCOL_PDF_COLUMN_CLASS}>
          <ProtocolAcroformSheet
            pdfUrl={`${API_BASE_URL}/maintenance/protocols/${protocolId}/source.pdf`}
            section={currentSection}
            values={localValues}
            disabled={readOnly}
            onValueChange={onValueChange}
          />
        </div>
      ) : null}

      {!isSummary && currentSection && !isAcroform && currentSection.id !== 'ink' ? (
        <Card className="w-full">
          <CardHeader className={MAINTENANCE_PROTOCOL_COLUMN_CLASS}>
            <CardTitle className="text-lg">{currentSection.title}</CardTitle>
          </CardHeader>
          <CardContent className={cn(MAINTENANCE_PROTOCOL_COLUMN_CLASS, 'divide-y pt-0')}>
            {currentSection.fields
              .slice()
              .sort((a, b) => a.sortOrder - b.sortOrder)
              .map((field) => (
                <ChecklistFieldInput
                  key={field.fieldKey}
                  field={field}
                  value={localValues[field.fieldKey]}
                  comment={localComments[field.fieldKey]}
                  disabled={readOnly}
                  onValueChange={(value) => onValueChange(field.fieldKey, value)}
                  onCommentChange={(comment) => onCommentChange(field.fieldKey, comment)}
                  onFileSelected={(file) => void onFileSelected(field.fieldKey, file)}
                />
              ))}
          </CardContent>
        </Card>
      ) : null}

      {isSummary ? (
        <div className={cn('space-y-4', MAINTENANCE_PROTOCOL_COLUMN_CLASS)}>
          <h2 className="text-lg font-semibold">Zusammenfassung</h2>
          <div className="space-y-3">
            {sections.map((section, index) => {
              const complete = protocolSectionIsComplete(section, editor, localValues);
              return (
                <button
                  key={section.id}
                  type="button"
                  className="flex w-full items-center justify-between gap-3 rounded-xl border bg-card p-4 text-left shadow-sm transition-colors hover:bg-muted/40"
                  onClick={() => setStep(index)}
                >
                  <span className="font-medium">{section.title}</span>
                  <span
                    className={cn(
                      'text-sm',
                      complete ? 'text-muted-foreground' : 'text-destructive',
                    )}
                  >
                    {complete ? 'vollständig' : 'offen'}
                  </span>
                </button>
              );
            })}
          </div>

          <Button
            type="button"
            variant="secondary"
            className="min-h-11 w-full sm:w-auto"
            disabled={validate.isPending}
            onClick={() => {
              void validate.mutateAsync().then((result) => {
                if (result.ok) {
                  setBlockingIssues([]);
                  toast.success('Alle Pflichtfelder und Unterschriften sind vollständig.');
                } else {
                  const messages = result.issues.map((issue) => issue.message);
                  setBlockingIssues(messages);
                  toast.error(messages.join(' '));
                }
              });
            }}
          >
            Abschluss prüfen
          </Button>
          {blockingIssues.length > 0 ? (
            <ul className="list-disc space-y-1 pl-5 text-sm text-destructive">
              {blockingIssues.map((message) => (
                <li key={message}>{message}</li>
              ))}
            </ul>
          ) : null}

          <Card>
            <CardContent className="space-y-4 pt-6">
              {readOnly ? (
                <TechnicianNotesCard notes={editor.generalNotes} />
              ) : (
                <div className="space-y-2">
                  <Label htmlFor="technician-notes">Anmerkungen des Monteurs</Label>
                  <textarea
                    id="technician-notes"
                    className="flex min-h-28 w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm"
                    maxLength={4000}
                    value={notes}
                    placeholder="Hinweise für Büro und Monteur"
                    onChange={(event) => {
                      setNotes(event.target.value);
                      setNotesDirty(true);
                    }}
                    onBlur={() => {
                      void persistNotes();
                    }}
                  />
                </div>
              )}
              <ProtocolSignaturesSection
                protocolId={protocolId}
                requireTechnicianSignature={editor.requireTechnicianSignature}
                requireCustomerSignature={editor.requireCustomerSignature}
                disabled={readOnly}
                existing={editor.signatures}
              />
            </CardContent>
          </Card>

          {editor.finalizationError ? (
            <p className="text-sm text-destructive">PDF-Fehler: {editor.finalizationError}</p>
          ) : null}

          {editor.workOrderStatus === 'completed' && editor.hasFinalPdf ? (
            <Button type="button" className="min-h-11 w-full sm:w-auto" asChild>
              <a
                href={`${API_BASE_URL}/maintenance/protocols/${protocolId}/final.pdf`}
                target="_blank"
                rel="noreferrer"
              >
                Abschluss-PDF öffnen
              </a>
            </Button>
          ) : null}

          {editor.workOrderStatus === 'in_progress' ||
          (editor.workOrderStatus === 'finalizing' && editor.finalizationError) ? (
            <Card>
              <CardContent className="space-y-3 pt-6">
                <div className="flex items-start gap-2">
                  <Checkbox
                    id="finalize-consent"
                    checked={finalizeConsent}
                    disabled={readOnly || finalize.isPending}
                    onCheckedChange={(v) => setFinalizeConsent(v === true)}
                  />
                  <Label htmlFor="finalize-consent" className="text-sm leading-snug">
                    Ich schließe das Protokoll verbindlich ab. Danach ist keine Bearbeitung mehr
                    möglich; das Abschluss-PDF wird erzeugt.
                  </Label>
                </div>
                <Button
                  type="button"
                  className="min-h-11 w-full"
                  disabled={!finalizeConsent || readOnly || finalize.isPending}
                  onClick={() => {
                    void (async () => {
                      if (notesDirty) {
                        await persistNotes();
                      }
                      await finalize.mutateAsync({
                        revision: editor.revision,
                        consentConfirmed: true,
                      });
                    })()
                      .then(() => {
                        setBlockingIssues([]);
                        toast.success('Finalisierung gestartet …');
                        onRefetch();
                      })
                      .catch((error: unknown) => {
                        const messages =
                          error instanceof ApiRequestError
                            ? protocolIssueMessages(error)
                            : ['Abschluss fehlgeschlagen.'];
                        setBlockingIssues(messages);
                        toast.error(messages.join(' '));
                      });
                  }}
                >
                  {editor.workOrderStatus === 'finalizing'
                    ? 'PDF-Erzeugung erneut starten'
                    : 'Verbindlich abschließen'}
                </Button>
              </CardContent>
            </Card>
          ) : null}

          {editor.workOrderStatus === 'finalizing' && !editor.finalizationError ? (
            <p className="text-sm text-muted-foreground">Abschluss-PDF wird erzeugt …</p>
          ) : null}
        </div>
      ) : null}

      <div
        className={cn(
          'hidden w-full gap-2 sm:flex',
          MAINTENANCE_PROTOCOL_COLUMN_CLASS,
          !isSummary && 'pt-2',
        )}
      >
        <WizardNavButtons
          step={step}
          totalSteps={totalSteps}
          sectionsLength={steps.length}
          onBack={() => setStep((s) => Math.max(0, s - 1))}
          onForward={() => setStep((s) => Math.min(totalSteps - 1, s + 1))}
        />
      </div>

      <div className="fixed inset-x-0 bottom-[calc(4.5rem+env(safe-area-inset-bottom))] z-30 border-t bg-background px-4 py-3 sm:hidden">
        <div className={cn('flex gap-2', MAINTENANCE_PROTOCOL_COLUMN_CLASS)}>
          <WizardNavButtons
            step={step}
            totalSteps={totalSteps}
            sectionsLength={steps.length}
            stretch
            onBack={() => setStep((s) => Math.max(0, s - 1))}
            onForward={() => setStep((s) => Math.min(totalSteps - 1, s + 1))}
          />
        </div>
      </div>
    </div>
  );
}

function buildLocalValues(
  editor: MaintenanceProtocolEditor,
  protocolId: string,
): Record<string, unknown> {
  const answers = Object.fromEntries(editor.answers.map((a) => [a.fieldKey, a.value]));
  const draft = readProtocolDraft(protocolId);
  const merged = { ...answers };
  for (const [key, entry] of Object.entries(draft)) {
    merged[key] = entry.value;
  }
  return merged;
}

function buildLocalComments(
  editor: MaintenanceProtocolEditor,
  protocolId: string,
): Record<string, string | null> {
  const comments = Object.fromEntries(editor.answers.map((a) => [a.fieldKey, a.comment ?? null]));
  const draft = readProtocolDraft(protocolId);
  for (const [key, entry] of Object.entries(draft)) {
    if (entry.comment !== undefined) {
      comments[key] = entry.comment;
    }
  }
  return comments;
}

export function MaintenanceProtocolWizardPage() {
  const { protocolId = '' } = useParams();
  const protocol = useMaintenanceProtocol(protocolId);
  const [editorEpoch, setEditorEpoch] = useState(0);
  usePageTitle('Wartungsprotokoll');

  const editor = protocol.data;
  const remountKey = editor ? `${protocolId}:${editorEpoch}` : protocolId;

  return (
    <div className="space-y-6">
      {protocol.isLoading ? <p className="text-sm text-muted-foreground">Lädt …</p> : null}
      {editor ? (
        <ProtocolWizardEditor
          key={remountKey}
          protocolId={protocolId}
          editor={editor}
          onRefetch={() => {
            setEditorEpoch((n) => n + 1);
            void protocol.refetch();
          }}
        />
      ) : null}
    </div>
  );
}
